import { useCallback } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import type { AppCode } from '../constants';
import { APP_LIST, MARKETING_PROCESS, MarketingProcessCode } from '../constants';
import type { FacebookAdTypeCode } from 'features/facebook/constants';
import { isFacebookAdTypeCode } from 'features/facebook/constants';

export const DEFAULT_MARKETING_PROCESS = MarketingProcessCode.SCALING;

const MARKETING_PROCESS_CODES: readonly string[] = MARKETING_PROCESS.map(
  ({ code }) => code,
);

const isMarketingProcessCode = (
  value: string | null | undefined,
): value is MarketingProcessCode =>
  !!value && MARKETING_PROCESS_CODES.includes(value);

const APP_CODES: readonly string[] = APP_LIST.map(({ code }) => code);

const isAppCode = (value: string | null | undefined): value is AppCode =>
  !!value && APP_CODES.includes(value);

/**
 * Keeps form/location state in the URL so links can preselect values and
 * back/forward navigation replays changes (setters use `pushIn` to create a
 * browser-history entry). Invalid or missing values fall back to defaults.
 *
 * To add a param: declare its `useQueryParam` below, derive a validated value
 * with a default, add a setter that skips no-op writes, and expose both from
 * the returned object.
 */
const useLocationParams = () => {
  const [rawMarketingProcess, setRawMarketingProcess] = useQueryParam(
    'marketingProcess',
    StringParam,
  );

  const marketingProcess = isMarketingProcessCode(rawMarketingProcess)
    ? rawMarketingProcess
    : DEFAULT_MARKETING_PROCESS;

  const setMarketingProcess = useCallback(
    (nextValue: MarketingProcessCode) => {
      if (nextValue === marketingProcess) return;
      setRawMarketingProcess(nextValue, 'pushIn');
    },
    [marketingProcess, setRawMarketingProcess],
  );

  const [rawApp, setRawApp] = useQueryParam('app', StringParam);

  // No default here: when the param is absent the app comes from the global
  // application selector (GlobalSettingsProvider).
  const app = isAppCode(rawApp) ? rawApp : undefined;

  const setApp = useCallback(
    (nextValue: AppCode) => {
      if (nextValue === app) return;
      setRawApp(nextValue, 'pushIn');
    },
    [app, setRawApp],
  );

  const [rawAdType, setRawAdType] = useQueryParam('ad_type', StringParam);

  // No default: when absent/invalid the form starts with an empty ad type.
  const adType = isFacebookAdTypeCode(rawAdType) ? rawAdType : undefined;

  const setAdType = useCallback(
    (nextValue: FacebookAdTypeCode | undefined) => {
      if (nextValue === adType) return;
      setRawAdType(nextValue, 'pushIn');
    },
    [adType, setRawAdType],
  );

  return {
    marketingProcess,
    setMarketingProcess,
    app,
    setApp,
    adType,
    setAdType,
  };
};

export default useLocationParams;
